import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { QuoteContext } from '../QuoteContext'
import { getEtfHoldingsRequest } from './api'
import { EFT_HOLDINGS_QUERY_KEY, FALLBACK_BREAKDOWN_RESPONSE, FALLBACK_STANDARD_RESPONSE } from './constants'
import {
  ETFHoldingDataType,
  ETFHoldingsBreakdownType,
  ETFHoldingsQuery,
  ETFHoldingsTopTenType,
  GetETFHoldingsResponseDataType,
} from './types'
import { getETFHoldingsRefreshInterval } from './utils'

export function useETFHoldingsQuery() {
  const quoteContext = React.useContext(QuoteContext)
  const [params] = useSearchParams()

  return React.useMemo(
    () => ({
      ticker: quoteContext.ticker,
      params: Object.fromEntries(params.entries()),
      [ETFHoldingsQuery.Breakdown]: (params.get(ETFHoldingsQuery.Breakdown) ??
        ETFHoldingsBreakdownType.PieChart) as ETFHoldingsBreakdownType,
      [ETFHoldingsQuery.TopTen]: (params.get(ETFHoldingsQuery.TopTen) ??
        ETFHoldingsTopTenType.Table) as ETFHoldingsTopTenType,
    }),
    [params, quoteContext.ticker]
  )
}

export function useRefetchData<Type extends ETFHoldingDataType>(ticker: string, type: Type) {
  const queryClient = useQueryClient()
  const dataVersion = React.useRef<number>()
  const queryKey = EFT_HOLDINGS_QUERY_KEY + type

  const etfHoldingsDataQuery = useQuery({
    queryKey: [queryKey],
    refetchInterval: getETFHoldingsRefreshInterval,
    staleTime: 1000,
    keepPreviousData: true,
    queryFn: async () => {
      const data = await getEtfHoldingsRequest(type, ticker)

      return (
        data ??
        queryClient.getQueryData<GetETFHoldingsResponseDataType<Type>>([queryKey]) ??
        ({
          ...(type === ETFHoldingDataType.Breakdown ? FALLBACK_BREAKDOWN_RESPONSE : FALLBACK_STANDARD_RESPONSE),
          version: dataVersion.current,
        } as GetETFHoldingsResponseDataType<Type>)
      )
    },
    onSuccess(data) {
      if (!dataVersion.current && data) {
        dataVersion.current = data.version
      }

      if (dataVersion.current !== data?.version) {
        window.location.reload()
        return
      }
    },
  })

  return etfHoldingsDataQuery
}
